import {
  RETRYABLE_STATUSES,
  resolveFetchPolicyOptions,
  type FetchPolicyDefaults,
} from './fetch-policy/defaults'
import {
  FetchPolicyError,
  normalizeFetchError,
  toAbortError,
  type FetchPolicyErrorKind,
} from './fetch-policy/errors'
import { sleep, toRequestInfo, toRequestMethod } from './fetch-policy/request'

export { FetchPolicyError }
export type { FetchPolicyErrorKind }

/**
 * Creates a fetch wrapper with timeout and retry behavior resolved at call time.
 *
 * @param options Runtime default resolver for timeout, retry count and backoff.
 * @returns Function that applies the policy to individual fetch requests.
 */
export const createFetchWithPolicy = ({
  resolveDefaults,
}: {
  resolveDefaults: () => FetchPolicyDefaults
}) => {
  return async ({
    request,
    init,
    timeoutMs,
    retries,
    retryDelayMs,
  }: {
    request: RequestInfo | URL
    init?: RequestInit
    timeoutMs?: number
    retries?: number
    retryDelayMs?: number
  }): Promise<Response> => {
    const defaults = resolveDefaults()
    const method = toRequestMethod(init?.method)
    const requestLabel = toRequestInfo(request)
    const {
      shouldRetryMethod,
      maxRetries,
      timeoutWindowMs,
      retryBackoffMs,
    } = resolveFetchPolicyOptions({
      defaults,
      method,
      timeoutMs,
      retries,
      retryDelayMs,
    })

    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(toAbortError('timeout')), timeoutWindowMs)
      const parentSignal = init?.signal
      const abortFromParent = () => controller.abort(toAbortError('signal'))
      parentSignal?.addEventListener('abort', abortFromParent, { once: true })

      try {
        const response = await fetch(request, {
          ...init,
          signal: controller.signal,
        })
        if (
          attempt < maxRetries &&
          shouldRetryMethod &&
          RETRYABLE_STATUSES.has(response.status)
        ) {
          await sleep(retryBackoffMs * (attempt + 1))
          continue
        }
        return response
      } catch (error) {
        const normalizedError = normalizeFetchError({
          error,
          signal: controller.signal,
          requestLabel,
        })
        if (attempt < maxRetries && shouldRetryMethod && normalizedError.retryable) {
          await sleep(retryBackoffMs * (attempt + 1))
          continue
        }
        throw normalizedError
      } finally {
        clearTimeout(timeout)
        parentSignal?.removeEventListener('abort', abortFromParent)
      }
    }

    throw new FetchPolicyError('network', `Request failed (${requestLabel})`, false)
  }
}
